import 'ag-charts-enterprise';
import type {
  AgAnnotation,
  AgAnnotationsEvent,
  AgChartInstance,
  AgFinancialChartOptions,
  AgPriceVolumeChartType,
  Listener,
} from 'ag-charts-enterprise';
import { AgFinancialCharts } from 'ag-charts-react';
import { addSeconds } from 'date-fns';
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { map, tap } from 'rxjs';
import { useElementSize, useObservableValue, useOHLCData } from '../../hooks';
import '../../init/agGrid';
import type { ResolutionEnumNoPrefix } from '../../types';
import { notEmpty, parseDate } from '../../utils';
import { type BoxProps, Flex, VStack } from '../Core';
import { type DateRangeOrLookback, DateRangePicker, LookbackOption, type ShortcutOption } from '../DateRangePicker';
import { type DateRange, getDateRangeFromLookback } from '../DateRangePicker/dateRange';
import { DropdownItem, DropdownMenu } from '../DropdownMenu';
import { FormControlSizes } from '../Form';
import { Icon, IconName } from '../Icons';
import { LoaderWrapper } from '../LoaderTalos';
import { Spinner } from '../Spinner';
import { SymbolDisplay, SymbolDisplaySize } from '../SymbolDisplay';
import { SymbolSelector, SymbolSelectorAppearance } from '../SymbolSelector';
import { TimePickerPrecision } from '../TimePicker';
import {
  AXIS_OFFSET,
  HeaderButton,
  HistoricalPriceChartHeader,
  HistoricalPriceChartWrapper,
  TOOLBAR_HEIGHT,
  TOOLBAR_WIDTH,
} from './styles';
import { useChartTheme } from './useChartTheme';

interface HistoricalPriceChartProps extends BoxProps {
  symbol: string;
  onChangeSymbol?: (symbol: string | undefined) => void;
  markets?: string[];
  enableToolbar?: boolean;
  rightAlignedItems?: ReactNode;
  leftAlignedItems?: ReactNode;
  resolution?: ResolutionEnumNoPrefix;
  availableResolutions?: ResolutionEnumNoPrefix[];
  onResolutionChange?: (newInterval: ResolutionEnumNoPrefix) => void;
  dateRange: DateRangeOrLookback | undefined;
  onDateRangeChange: (newDateRange: DateRangeOrLookback) => void;
  chartType?: AgPriceVolumeChartType;
  onChartTypeChange?: (newChartType: AgPriceVolumeChartType) => void;
  annotations?: AgAnnotation[];
  onAnnotationsChange?: (newAnnotations?: AgAnnotation[]) => void;
  showVolume?: boolean;
}
export const DEFAULT_OHLC_RESOLUTIONS: ResolutionEnumNoPrefix[] = ['1M', '5M', '1H', '4H', '1D'];
export const ALL_OHLC_RESOLUTIONS: ResolutionEnumNoPrefix[] = [
  '1M',
  '5M',
  '10M',
  '15M',
  '30M',
  '1H',
  '4H',
  '1D',
  '1W',
  '1MO',
];
/** A reasonable number of data points to fetch for each resolution.
 * ~288 data points for each resolution, except 1D (365), 1W (260 ≈ 5y), 1MO (120 ≈ 10y). */
const DEFAULT_TIMESPANS: Partial<Record<(typeof ALL_OHLC_RESOLUTIONS)[number], number>> = {
  '1M': 4.8 * 60 * 60, // 4.8h
  '5M': 24 * 60 * 60, // 24h
  '10M': 2 * 24 * 60 * 60, // 2d
  '15M': 3 * 24 * 60 * 60, // 3d
  '30M': 6 * 24 * 60 * 60, // 6d
  '1H': 12 * 24 * 60 * 60, // 12d
  '4H': 48 * 24 * 60 * 60, // 48d
  '1D': 365 * 24 * 60 * 60, // 365d
  '1W': 5 * 52 * 7 * 24 * 60 * 60, // ~5y
  '1MO': 10 * 12 * 30 * 24 * 60 * 60, // ~10y
};
const RESOLUTION_DURATION: Partial<Record<(typeof ALL_OHLC_RESOLUTIONS)[number], number>> = {
  '1M': 60,
  '5M': 5 * 60,
  '10M': 10 * 60,
  '15M': 15 * 60,
  '30M': 30 * 60,
  '1H': 60 * 60,
  '4H': 4 * 60 * 60,
  '1D': 24 * 60 * 60,
  '1W': 7 * 24 * 60 * 60,
  '1MO': 30 * 24 * 60 * 60,
};
const EXTRA_TIMESPAN_MULTIPLIER = 2;
const SHORTCUT_OPTIONS: ShortcutOption[] = [
  { value: LookbackOption.Past24Hours },
  { value: LookbackOption.PastWeek },
  { value: LookbackOption.Past30Days },
  { value: LookbackOption.Past90Days },
  { value: LookbackOption.YearToDate },
];
const CANDLESTICK_WIDTH = 10;
/** When more than this many resolution options exist, always show the dropdown menu. */
const MAX_RESOLUTION_BUTTONS = 8;
const RESOLUTION_LONG_LABELS: Partial<Record<(typeof ALL_OHLC_RESOLUTIONS)[number], string>> = {
  '1M': '1 Minute',
  '5M': '5 Minutes',
  '10M': '10 Minutes',
  '15M': '15 Minutes',
  '30M': '30 Minutes',
  '1H': '1 Hour',
  '4H': '4 Hours',
  '1D': '1 Day',
  '1W': '1 Week',
  '1MO': '1 Month',
};
const EMPTY_DATE_RANGE: DateRange = {
  from: null,
  to: null,
};

const RESOLUTION_DROPDOWN_TARGET_STYLE: CSSProperties = { height: `${TOOLBAR_HEIGHT}px` };

/** Below this width the resolution is shown in a dropdown and header padding/borders are omitted. */
const HEADER_MIN_WIDTH = 380;
const RESOLUTION_BUTTON_WIDTH = 40;

function getDefaultDataDateRange(
  width: number,
  resolution: ResolutionEnumNoPrefix
): { from: Date; to: null; lookback: number } {
  // Get lookback based on the width of the chart and the resolution
  const lookback = getLookback(width, resolution);
  // We overfetch data, so we can scroll back a bit before having to fetch more data
  const from = addSeconds(
    new Date(),
    -Math.max(lookback * EXTRA_TIMESPAN_MULTIPLIER, DEFAULT_TIMESPANS[resolution] ?? 0)
  );
  return { from, to: null, lookback };
}

function getLookback(width: number, resolution: ResolutionEnumNoPrefix): number {
  return (((width ?? 0) - TOOLBAR_WIDTH - AXIS_OFFSET) / CANDLESTICK_WIDTH) * (RESOLUTION_DURATION[resolution] ?? 0);
}

const DEFAULT_CHART_TYPE: AgPriceVolumeChartType = 'candlestick';

export function HistoricalPriceChart({
  symbol,
  onChangeSymbol,
  resolution = '1H',
  markets,
  enableToolbar = true,
  rightAlignedItems,
  leftAlignedItems,
  availableResolutions = DEFAULT_OHLC_RESOLUTIONS,
  onResolutionChange,
  dateRange: dateRangeOrLookback,
  annotations,
  onAnnotationsChange,
  chartType = DEFAULT_CHART_TYPE,
  onChartTypeChange,
  onDateRangeChange,
  showVolume = true,
  ...boxProps
}: HistoricalPriceChartProps) {
  const [isLoading, setIsLoading] = useState(true);
  const { elementRef: wrapperRef, size } = useElementSize<HTMLDivElement>({
    debounceWait: 100,
  });
  const chartRef = useRef<AgChartInstance>(null);
  const [dataDateRange, setDataDateRange] = useState<DateRange>(EMPTY_DATE_RANGE);
  const chartTheme = useChartTheme(symbol);

  // Set the data date range based on the date range value
  // Set the date range to a default date range based on the resolution if dateRangeOrLookback is not set
  useEffect(() => {
    if (!wrapperRef.current || !wrapperRef.current.offsetWidth) {
      return;
    }
    if (dateRangeOrLookback != null) {
      const lookback = getLookback(wrapperRef.current.offsetWidth, resolution);
      const { to, from } = getDateRangeFromLookback(dateRangeOrLookback);
      if (from) {
        setDataDateRange({ from: addSeconds(from, -lookback), to });
      }
      return;
    }
    setDefaultDates(wrapperRef.current.offsetWidth, resolution, setDataDateRange, onDateRangeChange);
  }, [resolution, onDateRangeChange, dateRangeOrLookback, wrapperRef]);

  const priceDataObservable = useOHLCData(
    dataDateRange
      ? {
          symbol,
          markets,
          startDate: dataDateRange.from ?? undefined,
          endDate: dataDateRange.to ?? undefined,
          resolution,
          liveUpdates: dataDateRange.to == null,
        }
      : undefined
  );

  const data = useObservableValue<AgFinancialChartOptions['data'] | undefined>(() => {
    return priceDataObservable.pipe(
      tap(() => {
        if (chartRef.current) {
          const state = chartRef.current.getState();
          // There's currently no event for when the chart type is changed, so we need to handle it manually
          if (chartType !== state.chartType) {
            onChartTypeChange?.(state.chartType ?? DEFAULT_CHART_TYPE);
          }
        }
      }),
      map(priceData => {
        if (priceData.length === 0) {
          return [];
        }
        setIsLoading(false);
        return priceData
          .map(d =>
            d.Volume !== '0' || d.Open !== '0' || d.High !== '0' || d.Low !== '0' || d.Close !== '0'
              ? {
                  date: parseDate(d.Timestamp),
                  open: parseFloat(d.Open),
                  high: parseFloat(d.High),
                  low: parseFloat(d.Low),
                  close: parseFloat(d.Close),
                  volume: parseFloat(d.Volume),
                }
              : {
                  date: parseDate(d.Timestamp),
                }
          )
          .filter(notEmpty);
      })
    );
  }, [priceDataObservable, chartType, onChartTypeChange]);

  // We don't have any context for the annotations, so we use never
  const handleAnnotations: Listener<AgAnnotationsEvent<never>> = useCallback(() => {
    // The annotations returned from getState are serializable so we can store them in a JSON format.
    const annotations = chartRef.current?.getState().annotations;
    onAnnotationsChange?.(annotations);
  }, [onAnnotationsChange, chartRef]);

  const options = useMemo(() => {
    return {
      theme: chartTheme,
      chartType: chartRef.current?.getState().chartType ?? chartType,
      height: size.offsetHeight ? size.offsetHeight - TOOLBAR_HEIGHT : undefined,
      rangeButtons: false,
      toolbar: enableToolbar,
      volume: showVolume,
      listeners: {
        annotations: handleAnnotations,
      },
      initialState: {
        annotations,
        zoom: {
          ratioX: {
            start: 1 / EXTRA_TIMESPAN_MULTIPLIER,
          },
        },
      },
      data,
    } satisfies AgFinancialChartOptions;
  }, [data, chartTheme, handleAnnotations, annotations, enableToolbar, chartType, showVolume, size.offsetHeight]);

  const handleResolutionChange = useCallback(
    (newResolution: ResolutionEnumNoPrefix) => {
      onResolutionChange?.(newResolution);
      setIsLoading(true);
      const { to, from, lookback } = getDefaultDataDateRange(wrapperRef.current?.offsetWidth ?? 0, newResolution);
      setDataDateRange({ from, to });
      onDateRangeChange({ from: addSeconds(new Date(), -lookback), to });
    },
    [onResolutionChange, onDateRangeChange, wrapperRef]
  );

  const handleDateRangeChange = useCallback(
    (newDateRange: DateRangeOrLookback) => {
      const dateRangeDates = getDateRangeFromLookback(newDateRange);

      if (!dateRangeDates.to && !dateRangeDates.from) {
        setDefaultDates(wrapperRef.current?.offsetWidth ?? 0, resolution, setDataDateRange, onDateRangeChange);
      } else {
        const lookback = getLookback(wrapperRef.current?.offsetWidth ?? 0, resolution);
        if (dateRangeDates.from) {
          setDataDateRange({
            from: addSeconds(dateRangeDates.from, -lookback * EXTRA_TIMESPAN_MULTIPLIER),
            to: dateRangeDates.to ?? new Date(),
          });
        }
        onDateRangeChange(newDateRange);
        const dates = getDateRangeFromLookback(newDateRange);
        if (chartRef.current && dates.from) {
          const currentState = chartRef.current.getState();
          // Set the zoom to fit the new date range
          chartRef.current.setState({
            version: currentState.version,
            zoom: {
              rangeX: {
                start: {
                  __type: 'date',
                  value: dates.from.toISOString(),
                },
              },
            },
          });
        }
      }
    },
    [onDateRangeChange, resolution, wrapperRef]
  );

  const isReturnedDataEmpty = useMemo(() => {
    if (data == null) {
      return false;
    }
    return !data.some(d => d.open != null && d.high != null && d.low != null && d.close != null && d.volume != null);
  }, [data]);

  const headerWidth = size.offsetWidth ?? 0;
  const showResolutionDropdown =
    availableResolutions.length > MAX_RESOLUTION_BUTTONS ||
    headerWidth < HEADER_MIN_WIDTH + availableResolutions.length * RESOLUTION_BUTTON_WIDTH;

  return (
    <HistoricalPriceChartWrapper ref={wrapperRef} data-testid="historical-price-chart-wrapper" {...boxProps}>
      <HistoricalPriceChartHeader>
        <Flex
          alignItems="center"
          px="spacing12"
          color="colorTextImportant"
          whiteSpace="nowrap"
          data-testid="symbol-display"
        >
          {onChangeSymbol ? (
            <SymbolSelector
              appearance={SymbolSelectorAppearance.CurrencyPair}
              symbol={symbol}
              onSymbolChanged={onChangeSymbol}
            />
          ) : (
            <SymbolDisplay symbol={symbol} showDescription={false} p="0" size={SymbolDisplaySize.Medium} />
          )}
        </Flex>
        <Flex flex="0 1 auto" overflow="hidden" alignItems="center" minWidth="spacing32">
          {leftAlignedItems}
        </Flex>
        <Flex
          px="spacing12"
          flex="1 1 auto"
          alignItems="center"
          justifyContent="flex-end"
          borderRight="solid 1px"
          borderColor="backgroundBody"
          ml="auto"
        >
          <DateRangePicker
            shortcuts={SHORTCUT_OPTIONS}
            clearable={true}
            size={FormControlSizes.Small}
            value={dateRangeOrLookback ?? EMPTY_DATE_RANGE}
            onChange={handleDateRangeChange}
            maxTimePickerPrecision={TimePickerPrecision.MINUTE}
            data-testid="date-range-picker"
            dropdownPlacement="bottom-end"
            // Can't pick a date in the future
            maxValue={new Date()}
            width="min(240px, 100%)"
          />
        </Flex>
        <Flex flex="0 1 auto" minWidth="max-content" overflow="hidden" alignItems="center">
          {availableResolutions?.length ? (
            showResolutionDropdown ? (
              <DropdownMenu
                placement="bottom-end"
                usePortal
                targetStyle={RESOLUTION_DROPDOWN_TARGET_STYLE}
                triggerElement={
                  <HeaderButton active={false} data-testid="resolution-dropdown-trigger">
                    {resolution}
                  </HeaderButton>
                }
                dropdownProps={{ 'data-testid': 'resolution-dropdown' }}
              >
                {availableResolutions.map(res => (
                  <DropdownItem
                    key={res}
                    onClick={() => handleResolutionChange(res)}
                    data-testid={`resolution-dropdown-item-${res}`}
                  >
                    {RESOLUTION_LONG_LABELS[res] ?? res}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            ) : (
              availableResolutions.map(res => (
                <HeaderButton
                  key={res}
                  active={resolution === res}
                  onClick={() => handleResolutionChange(res)}
                  data-active={resolution === res}
                  data-testid={`resolution-button-${res}`}
                >
                  {res}
                </HeaderButton>
              ))
            )
          ) : null}
        </Flex>
        <Flex flex="0 0 auto" overflow="hidden" alignItems="center">
          {rightAlignedItems}
        </Flex>
      </HistoricalPriceChartHeader>
      {!isLoading && options ? (
        isReturnedDataEmpty ? (
          <VStack flex={1} gap="spacing16">
            <Icon icon={IconName.ExclamationCircle} size={32} />
            <Flex color="colorTextImportant">No data found</Flex>
          </VStack>
        ) : (
          <AgFinancialCharts ref={chartRef} options={options} />
        )
      ) : (
        <LoaderWrapper>
          <Spinner />
        </LoaderWrapper>
      )}
    </HistoricalPriceChartWrapper>
  );
}

function setDefaultDates(
  containerWidth: number,
  resolution: ResolutionEnumNoPrefix,
  setDataDateRange: (newDateRange: DateRange) => void,
  onDateRangeChange: (newDateRange: DateRangeOrLookback) => void
) {
  const { from, to, lookback } = getDefaultDataDateRange(containerWidth, resolution);
  setDataDateRange({ from, to });
  // Update the date range to what the user is actually seeing on the chart
  onDateRangeChange({
    from: addSeconds(new Date(), -lookback),
    to,
  });
}
